import { useState, useEffect, useRef } from 'react'
import { useTheme } from '../context/ThemeContext'

/**
 * Modal for creating/editing scheduled workovers
 * Modes: 'drop' (from queue drag), 'create' (+ button), 'edit' (click existing bar)
 */
function ScheduleModal({ isOpen, onClose, onSave, onDelete, mode, initialData = {}, rigs = [], allWells = [] }) {
    const { theme } = useTheme()
    const isDark = theme === 'dark'
    const wellInputRef = useRef(null)

    const [form, setForm] = useState({
        well: '', rig: '', startDate: '', duration: 7,
        workType: '', reason: '', estCost: '', defBopd: '', status: 'Pending'
    })
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)
    const [confirmDelete, setConfirmDelete] = useState(false)

    // Reset form when modal opens
    useEffect(() => {
        if (!isOpen) return
        const today = new Date().toISOString().split('T')[0]

        if (mode === 'edit') {
            const start = initialData.startDate || today
            const end = initialData.endDate
            // Use T12:00 to avoid timezone off-by-one
            const dur = start && end
                ? Math.max(1, Math.round((new Date(end + 'T12:00') - new Date(start + 'T12:00')) / (1000 * 60 * 60 * 24)))
                : 7
            setForm({
                well: initialData.well || '',
                rig: initialData.rig || '',
                startDate: start,
                duration: dur,
                workType: initialData.workType || '',
                reason: initialData.reason || '',
                estCost: initialData.estCost || initialData.finalCost || '',
                defBopd: initialData.defBopd || '',
                status: initialData.status || 'Pending'
            })
        } else if (mode === 'drop') {
            setForm({
                well: initialData.wellName || initialData.well || '',
                rig: initialData.rig || '',
                startDate: today,
                duration: 7,
                workType: initialData.workType || '',
                reason: initialData.reason || '',
                estCost: initialData.costEstimate || '',
                defBopd: initialData.bopdImpact || '',
                status: 'Pending'
            })
        } else {
            setForm({
                well: '', rig: '', startDate: today, duration: 7,
                workType: '', reason: '', estCost: '', defBopd: '', status: 'Pending'
            })
        }
        setError(null)
        setConfirmDelete(false)
    }, [isOpen, mode, initialData])

    // Focus well input for create mode
    useEffect(() => {
        if (isOpen && mode === 'create' && wellInputRef.current) {
            setTimeout(() => wellInputRef.current.focus(), 100)
        }
    }, [isOpen, mode])

    if (!isOpen) return null

    const endDate = (() => {
        if (!form.startDate || !form.duration) return '--'
        const d = new Date(form.startDate + 'T12:00')
        d.setDate(d.getDate() + parseInt(form.duration))
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    })()

    const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!form.well.trim() || !form.rig || !form.startDate) {
            setError('Well, rig, and start date are required')
            return
        }

        const endD = new Date(form.startDate + 'T12:00')
        endD.setDate(endD.getDate() + parseInt(form.duration || 7))

        setSaving(true)
        setError(null)
        try {
            await onSave({
                ...form,
                endDate: endD.toISOString().split('T')[0],
                estCost: form.estCost ? parseFloat(form.estCost) : null,
                defBopd: form.defBopd ? parseFloat(form.defBopd) : null,
                id: mode === 'edit' ? initialData.id : undefined,
                mode
            })
            onClose()
        } catch (err) {
            setError(err.message || 'Failed to save')
        } finally {
            setSaving(false)
        }
    }

    const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${
        isDark
            ? 'bg-slate-800 border-slate-600 text-gray-200 focus:border-blue-500'
            : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
    } focus:outline-none focus:ring-1 focus:ring-blue-500`

    const labelCls = `block text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="fixed inset-0 bg-black/50" />
            <div
                className={`relative w-full max-w-lg rounded-xl shadow-2xl ${
                    isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-gray-200'
                }`}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {mode === 'edit' ? 'Edit Workover' : mode === 'drop' ? 'Schedule from Queue' : 'Schedule New Workover'}
                    </h3>
                    <button onClick={onClose} className={`text-xl leading-none px-1 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-800'}`}>
                        &times;
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
                    {/* Well Name */}
                    <div>
                        <label className={labelCls}>Well Name *</label>
                        {mode === 'create' ? (
                            <>
                                <input
                                    ref={wellInputRef}
                                    type="text"
                                    list="well-options"
                                    value={form.well}
                                    onChange={e => update('well', e.target.value)}
                                    placeholder="Search wells..."
                                    className={inputCls}
                                    required
                                />
                                <datalist id="well-options">
                                    {allWells.map((w, i) => <option key={i} value={w} />)}
                                </datalist>
                            </>
                        ) : (
                            <input type="text" value={form.well} onChange={e => update('well', e.target.value)} className={inputCls} required />
                        )}
                    </div>

                    {/* Rig + Status (edit) / Start Date (create/drop) */}
                    <div className={`grid gap-3 ${mode === 'edit' ? 'grid-cols-3' : 'grid-cols-2'}`}>
                        <div>
                            <label className={labelCls}>Rig *</label>
                            <select value={form.rig} onChange={e => update('rig', e.target.value)} className={inputCls} required>
                                <option value="">Select rig...</option>
                                {rigs.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                            </select>
                        </div>
                        {mode === 'edit' && (
                            <div>
                                <label className={labelCls}>Status</label>
                                <select value={form.status} onChange={e => update('status', e.target.value)} className={inputCls}>
                                    <option value="Pending">Pending</option>
                                    <option value="Active">Active</option>
                                    <option value="Completed">Completed</option>
                                </select>
                            </div>
                        )}
                        <div>
                            <label className={labelCls}>Start Date *</label>
                            <input type="date" value={form.startDate} onChange={e => update('startDate', e.target.value)} className={inputCls} required />
                        </div>
                    </div>

                    {/* Duration + End Date */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Duration (days)</label>
                            <input type="number" min="1" max="365" value={form.duration} onChange={e => update('duration', e.target.value)} className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>End Date</label>
                            <div className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-slate-800/50 border-slate-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                                {endDate}
                            </div>
                        </div>
                    </div>

                    {/* Work Type + Reason */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Work Type</label>
                            <input type="text" value={form.workType} onChange={e => update('workType', e.target.value)} placeholder="Pump Change, Workover..." className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Reason</label>
                            <input type="text" value={form.reason} onChange={e => update('reason', e.target.value)} placeholder="Downhole Failure..." className={inputCls} />
                        </div>
                    </div>

                    {/* Cost + BOPD */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Estimated Cost ($)</label>
                            <input type="number" step="0.01" value={form.estCost} onChange={e => update('estCost', e.target.value)} placeholder="0.00" className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>BOPD Impact</label>
                            <input type="number" step="0.1" value={form.defBopd} onChange={e => update('defBopd', e.target.value)} placeholder="0.0" className={inputCls} />
                        </div>
                    </div>

                    {error && (
                        <div className="text-xs text-red-400 bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    {/* Footer */}
                    <div className={`flex justify-between pt-3 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2">
                            {mode === 'edit' && onDelete && !confirmDelete && (
                                <button type="button" disabled={saving}
                                    onClick={() => setConfirmDelete(true)}
                                    className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                                    Delete
                                </button>
                            )}
                            {mode === 'edit' && onDelete && confirmDelete && (
                                <>
                                    <span className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>Sure?</span>
                                    <button type="button" disabled={saving}
                                        onClick={async () => {
                                            setSaving(true)
                                            setError(null)
                                            try {
                                                await onDelete(initialData.id)
                                                onClose()
                                            } catch (err) {
                                                setError(err.message || 'Failed to delete')
                                            } finally {
                                                setSaving(false)
                                            }
                                        }}
                                        className="px-3 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                                        {saving ? 'Deleting...' : 'Yes, Delete'}
                                    </button>
                                    <button type="button" onClick={() => setConfirmDelete(false)}
                                        className={`px-3 py-2 text-sm rounded-lg ${isDark ? 'bg-slate-800 text-gray-300 hover:bg-slate-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                                        No
                                    </button>
                                </>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <button type="button" onClick={onClose}
                                className={`px-4 py-2 text-sm rounded-lg ${isDark ? 'bg-slate-800 text-gray-300 hover:bg-slate-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                                Cancel
                            </button>
                            <button type="submit" disabled={saving}
                                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                                {saving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Schedule'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default ScheduleModal
